import { Person } from '../../types';

interface PersonCardProps {
  person: Person;
  onClick: () => void;
  onDragStart?: (person: Person) => void;
}

export const PersonCard: React.FC<PersonCardProps> = ({ person, onClick, onDragStart }) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('person', JSON.stringify(person));
    onDragStart?.(person);
  };
  const initials = person.Name
    ? person.Name.split(' ').map(n => n[0]).join('')
    : '??';

  const canonicalSkills = person.CanonicalSkills ?? [];
  const hasMappedSkills = canonicalSkills.length > 0;
  const canonicalPreview = hasMappedSkills ? canonicalSkills.slice(0, 3) : [];
  const canonicalMore = hasMappedSkills ? Math.max(0, canonicalSkills.length - canonicalPreview.length) : 0;

  const expertiseList =
    !hasMappedSkills && person.Expertise
      ? person.Expertise.split(/[,;]/).slice(0, 2).map(e => e.trim()).filter(e => e)
      : [];

  const aiToolsList = person.AIToolExperience
    ? person.AIToolExperience.split(/[,;]/).slice(0, 2).map(t => t.trim()).filter(t => t)
    : [];

  const additionalSkillsList =
    !hasMappedSkills && person.AdditionalSkills
      ? person.AdditionalSkills.split(/[,;]/).slice(0, 2).map(s => s.trim()).filter(s => s)
      : [];

  const professionalInterestsList = person.ProfessionalInterests
    ? person.ProfessionalInterests.split(/[,;]/).slice(0, 2).map(i => i.trim()).filter(i => i)
    : [];

  const expertiseCount = person.Expertise ? person.Expertise.split(/[,;]/).length : 0;
  const aiToolsCount = person.AIToolExperience ? person.AIToolExperience.split(/[,;]/).length : 0;
  const additionalSkillsCount = person.AdditionalSkills ? person.AdditionalSkills.split(/[,;]/).length : 0;
  const professionalInterestsCount = person.ProfessionalInterests ? person.ProfessionalInterests.split(/[,;]/).length : 0;

  return (
    <div
      data-testid="person-card"
      className="bg-dark-card border border-dark-border rounded-xl shadow-sm hover-lift overflow-hidden cursor-move transition-all duration-300 hover:border-brand-blue/30 hover:shadow-lg"
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
    >
      <div className="p-6">
        {/* Header with avatar and basic info */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-14 h-14 bg-brand-gradient rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg text-dark-text mb-1 hover:text-brand-blue transition-colors">
              {person.Name || 'Unknown'}
            </h3>
            <p className="text-dark-text-secondary font-medium text-sm mb-2">
              {person.Title || 'Title not specified'}
            </p>
            <div className="flex items-center gap-2 text-xs text-dark-text-secondary flex-wrap">
              {person.Team && (
                <span className="inline-flex items-center whitespace-nowrap max-w-[11rem] truncate" title={person.Team}>
                  <span className="truncate">{person.Team}</span>
                </span>
              )}
              {person.ManagementLevel && (
                <span className="px-1.5 py-0.5 rounded bg-dark-border text-dark-text-secondary font-medium" title="Management level">
                  {person.ManagementLevel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Skills and Experience Tags */}
        <div className="mb-4 space-y-2">
          {hasMappedSkills && (
            <div className="flex flex-wrap gap-1" title="Normalized catalog skills">
              {canonicalPreview.map((s) => (
                <span key={s.id} className="px-2 py-1 bg-brand-gradient text-white text-xs font-medium rounded-md">
                  {s.name}
                </span>
              ))}
              {canonicalMore > 0 && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded-md">
                  +{canonicalMore} skills
                </span>
              )}
            </div>
          )}

          {expertiseList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {expertiseList.map((exp, i) => (
                <span key={i} className="px-2 py-1 bg-research-gradient text-white text-xs font-medium rounded-md">
                  {exp}
                </span>
              ))}
              {expertiseCount > 2 && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded-md">
                  +{expertiseCount - 2} expertise
                </span>
              )}
            </div>
          )}

          {additionalSkillsList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {additionalSkillsList.map((skill, i) => (
                <span key={i} className="px-2 py-1 bg-validation-gradient text-white text-xs font-medium rounded-md">
                  {skill}
                </span>
              ))}
              {additionalSkillsCount > 2 && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded-md">
                  +{additionalSkillsCount - 2} skills
                </span>
              )}
            </div>
          )}

          {aiToolsList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {aiToolsList.map((tool, i) => (
                <span key={i} className="px-2 py-1 bg-ai-gradient text-white text-xs font-medium rounded-md">
                  {tool}
                </span>
              ))}
              {aiToolsCount > 2 && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded-md">
                  +{aiToolsCount - 2} AI tools
                </span>
              )}
            </div>
          )}

          {professionalInterestsList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {professionalInterestsList.map((interest, i) => (
                <span key={i} className="px-2 py-1 bg-brand-gradient text-white text-xs font-medium rounded-md">
                  💡 {interest}
                </span>
              ))}
              {professionalInterestsCount > 2 && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs font-medium rounded-md">
                  +{professionalInterestsCount - 2} interests
                </span>
              )}
            </div>
          )}
        </div>

        {/* Job responsibilities preview */}
        {person.JobResponsibilities && (
          <div className="mb-4">
            <p className="text-xs text-dark-text-secondary line-clamp-2 leading-relaxed">
              {person.JobResponsibilities.length > 120
                ? person.JobResponsibilities.substring(0, 120) + '...'
                : person.JobResponsibilities}
            </p>
          </div>
        )}

        {person.PrimaryLocation && (
          <div className="mb-4">
            <span className="inline-flex items-center gap-1.5 text-xs text-dark-text-secondary" title={person.PrimaryLocation}>
              <span className="text-brand-green">📍</span>
              <span className="truncate">{person.PrimaryLocation}</span>
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="pt-3 border-t border-dark-border">
          <div className="flex justify-between items-center">
            <span className="text-xs text-brand-blue font-medium">View Full Profile →</span>
            <div className="flex space-x-1">
              {person.Languages && <span className="w-2 h-2 bg-brand-yellow rounded-full" title="Multilingual"></span>}
              {person.Contact && <span className="w-2 h-2 bg-brand-green rounded-full" title="Contact Available"></span>}
              {(hasMappedSkills || expertiseList.length > 0 || additionalSkillsList.length > 0) && (
                <span className="w-2 h-2 bg-brand-orange rounded-full" title="Skills"></span>
              )}
              {aiToolsList.length > 0 && <span className="w-2 h-2 bg-brand-blue rounded-full" title="AI Experience"></span>}
              {professionalInterestsList.length > 0 && <span className="w-2 h-2 bg-purple-500 rounded-full" title="Professional Interests"></span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};




